import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {type DraggableData, type DraggableEvent} from 'react-draggable';

const BOTTOM_OFFSET = 80;
const RIGHT_OFFSET = 24;

export function useBoundedDrag(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({x: 0, y: 0});

  const clampToContainer = useCallback(() => {
    if (!containerRef.current || !nodeRef.current) {
      return;
    }
    const {clientWidth: cw, clientHeight: ch} = containerRef.current;
    const {offsetWidth: nw, offsetHeight: nh} = nodeRef.current;

    setPosition(prev => ({
      x: Math.max(0, Math.min(prev.x, cw - nw)),
      y: Math.max(0, Math.min(prev.y, ch - nh)),
    }));
  }, []);

  useLayoutEffect(() => {
    if (!active || !containerRef.current || !nodeRef.current) {
      return;
    }
    const {clientWidth: cw, clientHeight: ch} = containerRef.current;
    const {offsetWidth: nw, offsetHeight: nh} = nodeRef.current;

    setPosition({
      x: Math.max(0, cw - RIGHT_OFFSET - nw),
      y: Math.max(0, ch - BOTTOM_OFFSET - nh),
    });
  }, [active]);

  useEffect(() => {
    if (!active) {
      return;
    }

    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => clampToContainer());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [active, clampToContainer]);

  const handleDrag = useCallback((_e: DraggableEvent, data: DraggableData) => {
    setPosition({x: data.x, y: data.y});
  }, []);

  return {containerRef, nodeRef, position, handleDrag};
}
